//
//  src/components/enquire/EnquireForm.js
//
import EnquireReferrer from './EnquireReferrer';
import React, { useEffect, useState } from "react";
import { persistGclidFromSearch, readStoredGclid } from '../../utils/Gclid';
import { useNavigate }   from "react-router-dom";
import { useDropzone }   from 'react-dropzone';
import { useMediaQuery } from 'react-responsive';
import M from "materialize-css/dist/js/materialize.min.js";
import './Enquire.css';


//
//  enquire form var[s]
//
const MAX_FILE_SIZE = 5;
const MAX_FILES = 4;


//
//  enquire form dropzone
//
function MultiFileDropzone({ onFilesChange }) {

    const [error, setError] = useState(null);

    const isMobile = useMediaQuery({ query: '(max-width: 800px)' });

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'image/*': []
        },
        maxFiles: MAX_FILES,
        maxSize: MAX_FILE_SIZE * 1024 * 1024,
        multiple: true,
        onDrop: acceptedFiles => {
            const mapped = acceptedFiles.map(file =>
                Object.assign(file, { preview: URL.createObjectURL(file) })
            );
            onFilesChange(prev => [...prev, ...mapped]);
        },
        onDropAccepted: () => setError([]),
        onDropRejected: (fileRejections) => {
            fileRejections.forEach(({ file, errors }) => {
                errors.forEach(err => {
                    if (err.code === 'file-too-large') {
                        setError(`File is too large — max size is ${MAX_FILE_SIZE} MB.`);
                    } else {
                        setError(`${err.message}`);
                    }
                });
            });
        }
    });

    return (
        <div {...getRootProps()} className="dropzone">
            <input {...getInputProps()} />
            {isDragActive ? (
                <p>Drop images here…</p>
            ) : (
                <>
                    {isMobile ? (
                        <p>Tap to attach images</p>
                    ) : (
                        <p>Drag & drop images here, or click to browse your files</p>
                    )}
                </>
            )}
            {error}
        </div>
    );

}


//
//  enquire form
//
export default function EnquireForm(props) {

    const navigate = useNavigate();

    const [data, setData] = useState({
        'from_name': "",
        'from_name_first': "",
        'from_name_last': "",
        'from_email': "",
        'message': "",
        'referrer_code': "",
        'type': "message",
        'marketing_opt_in': false
    });

    const [isDisabled, setDisabled] = useState(true);
    const [isLoading, setLoading] = useState(false);
    const [isReferred, setReferred] = useState(true);

    const [files, setFiles] = useState([]);
    const [gclid, setGclid] = useState(null);

    const referrers = ["L337HACKER"];

    useEffect(() => {
        persistGclidFromSearch(window.location.search);
        const params = new URLSearchParams(window.location.search);
        const gclidParam = params.get('gclid');
        if (gclidParam) {
            setGclid(gclidParam);
            return;
        }
        const stored = readStoredGclid();
        if (stored) {
            setGclid(stored);
        }
    }, []);

    const handleFilesChange = (newFiles) => {
        if (files.length < MAX_FILES) {
            setFiles(newFiles);
        }
    };

    const removeFile = (index) => {
        setFiles(prev => {
            const updated = prev.filter((_, i) => i !== index);
            return updated;
        });
    };

    const send = (e) => {

        // prevent default
        e.preventDefault();

        // disable form
        setDisabled(true);

        // validate email
        if (!validate(data.from_email)) {
            setDisabled(false);
            return false;
        }

        // build form Data
        const formData = new FormData();

        // build form data append values
        Object.entries(data).forEach(([key, value]) => {
            if (key === 'marketing_opt_in') {
                formData.append(key, value ? '1' : '0');
                return;
            }
            if (value) formData.append(key, value);
        });

        // append gclid if available
        if (gclid) {
            formData.append('gclid', gclid);
        }

        // build form data append files
        files.forEach(file => formData.append('attachments[]', file));

        // set loading
        setLoading(true);

        // send request
        fetch(`${process.env.REACT_APP_API_BASE}/guest/contact/form`, {
            method: 'POST',
            body: formData,
        }).then(response => {
            if (!response.ok) {
                return response.json().then(err => Promise.reject(err));
            }
            return response.json();
        })
        .then(sendSuccess)
        .catch(sendError);

    };

    const sendError = (error) => {
        console.error(error)
        const formDataCopy = { ...data };
        setData(data);
        setDisabled(true)
        setLoading(false);
        M.toast({ html: 'Network error. Please try again.', classes: 'red' });
        navigate("/contact/form/error/", {
            state: {
                data: formDataCopy,
                files: files.length
            }
        });
    }

    const sendSuccess = (responseData) => {
        const emptyData = {}
        const formDataCopy = { ...data };
        setData(emptyData);
        setDisabled(false)
        setLoading(false);
        M.toast({ html: 'Thank you for your enquiry! We will get back to you shortly.', classes: 'green' });
        navigate("/contact/form/sent/", {
            state: {
                data: formDataCopy,
                files: files.length
            }
        });
    }

    const set = (key, value) => {

        // set data[key] value
        data[key] = value

        // keep combined from_name in sync with first/last
        if (key === 'from_name_first' || key === 'from_name_last') {
            data['from_name'] = `${data['from_name_first'] || ''} ${data['from_name_last'] || ''}`.trim()
        }

        // set file
        if (key === 'file') {
            data.file = value;
        }

        // set message[referrer]
        if (data['message']) {
            let referrer_code = referrers.find(item => data['message'].toLocaleLowerCase().includes(item.toLowerCase()));
            if (referrer_code) {
                data['referrer_code'] = referrer_code;
                setReferred(true);
            } else {
                data['referrer_code'] = '';
                setReferred(false);
            }
        } else {
            data['referrer_code'] = '';
            setReferred(false);
        }

        // validate
        if (validate(data['from_email'])) {
            setDisabled(false)
        }

        // set data
        setData(data);

    }

    const submit = (event) => {
        event.preventDefault();
    };

    const validate = (email) => {
        return email.match(
            // eslint-disable-next-line
            /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
        );
    }

    useEffect(() => {

        // materialize
        M.updateTextFields()

    });

    useEffect(() => {
        return () => files.forEach(f => URL.revokeObjectURL(f.preview));
    }, [files]);

    return (
        <div className="valign-wrapper">
            <div className="enquire-form">
                <div className="row">
                    <form className="col s12" onSubmit={submit}>
                        <div className="row">
                            <div className="">
                                <h1 className="enquire-title">Send a Message</h1>
                                <div className="action-text">
                                    Whether you're curious about pricing, want to discuss a design idea, book a tattoo or just have a question about the process, no question is too small.
                                </div>
                            </div>
                        </div>
                        <br /><br /><br />
                        <div className="row">
                            <div className="input-field col s6">
                                <input id="first_name" onChange={(e) => { set('from_name_first', e.target.value) }} placeholder="First Name" type="text" className="validate" />
                                <label htmlFor="first_name">First Name</label>
                            </div>
                            <div className="input-field col s6">
                                <input id="last_name" onChange={(e) => { set('from_name_last', e.target.value) }} placeholder="Last Name" type="text" className="validate" />
                                <label htmlFor="last_name">Last Name</label>
                            </div>
                        </div>
                        <div className="row">
                            <div className="input-field col s12">
                                <input id="email" onChange={(e) => { set('from_email', e.target.value) }} placeholder="E-Mail" type="email" className="validate" required />
                                <label htmlFor="email">Email</label>
                            </div>
                        </div>
                        {isReferred && data['referrer_code'] !== "" ? (
                            <EnquireReferrer code={data['referrer_code']} />
                        ) : (
                            <></>
                        )}
                        <div className="row">
                            <div className="input-field col s12">
                                <textarea id="message" onChange={(e) => { set('message', e.target.value) }} placeholder="Message" className="materialize-textarea">
                                </textarea>
                                <label htmlFor="message">Message</label>
                            </div>
                        </div>
                        <div className="row">
                            {files.length < MAX_FILES && (
                                <MultiFileDropzone onFilesChange={handleFilesChange} />
                            )}
                            <div className="thumbnails">
                                {files.map((file, i) => (
                                    <div key={i} className="thumb">
                                        <button
                                            type="button"
                                            className="remove-btn"
                                            onClick={() => removeFile(i)}
                                        >
                                            ×
                                        </button>
                                        <img src={file.preview} alt={file.name} />
                                        <p>{file.name}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="row">
                            <label className={`enquire-optin ${data.marketing_opt_in ? "checked" : ""}`}>
                                <input
                                    type="checkbox"
                                    checked={!!data.marketing_opt_in}
                                    onChange={(e) => {
                                        setData(prev => ({ ...prev, marketing_opt_in: e.target.checked }));
                                    }}
                                />
                                <span className="enquire-optin-box" aria-hidden="true">
                                    <svg viewBox="0 0 24 24" width="14" height="14">
                                        <path d="M5 12l5 5L20 7" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                                <span className="enquire-optin-text">
                                    <strong>
                                        Get <span className="enquire-optin-highlight">special pricing</span> and <span className="enquire-optin-highlight">discounts</span> when they come up.
                                    </strong>
                                    <span className="enquire-optin-sub">
                                        I only reach out when I've got real value to share, whether that's a last-minute cancellation, a special rate, or a serious discount on a session. If it doesn't save you money, you won't hear from me. No spam, unsubscribe anytime.
                                    </span>
                                </span>
                            </label>
                        </div>
                        <div className="row">
                            <button id="submit" className="btn waves-effect waves-light orange" onClick={send} name="action" disabled={isDisabled} >
                                {isLoading ? 'Processing...' : 'Send Message'}
                            </button>
                        </div>
                        <br />
                        <div className="row">
                            <p className="waiver">
                                By submitting this form, you confirm that you have read, understood, and agreed to our <a href="/legal/terms-and-conditions/" title="terms and conditions">Terms and Conditions</a> and <a href="/legal/privacy-policy/" title="privacy policy">Privacy Policy</a>.
                            </p>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );

}
